(() => {
  'use strict';

  if (customElements.get('custom-native-share')) return;

  class CustomNativeShare extends HTMLElement {
    constructor() {
      super();

      this.trigger = this.querySelector(
        '[data-native-share-trigger]'
      );

      this.modal = this.querySelector(
        '[data-native-share-modal]'
      );

      this.dialog = this.querySelector(
        '[data-native-share-dialog]'
      );

      this.actionButtons = Array.from(
        this.querySelectorAll(
          '[data-native-share-action]'
        )
      );

      this.nativeShareButton = this.querySelector(
        '[data-native-share-action="native"]'
      );

      this.status = this.querySelector(
        '[data-native-share-status]'
      );

      this.displayMode =
        this.dataset.displayMode || 'popup';

      this.title =
        this.dataset.shareTitle || document.title;

      const rawUrl =
        this.dataset.shareUrl || '';

      /*
       * Use the short redirect URL directly.
       * No fetch check and no fallback to the original page URL.
       */
      this.url = this.getReadableUrl(rawUrl);

      /*
       * Messenger receives an encoded URL only.
       */
      this.messengerUrl =
        this.getMessengerUrl(rawUrl);

      this.copySuccessText =
        this.dataset.copySuccess || 'تم نسخ الرابط';

      this.copyErrorText =
        this.dataset.copyError || 'تعذر نسخ الرابط';

      this.nativeShareErrorText =
        this.dataset.nativeShareError ||
        'تعذر فتح خيارات المشاركة';

      this.previouslyFocusedElement = null;
      this.statusTimer = null;

      this.handleTriggerClick =
        this.handleTriggerClick.bind(this);

      this.handleModalClick =
        this.handleModalClick.bind(this);

      this.handleKeydown =
        this.handleKeydown.bind(this);

      this.handleActionClick =
        this.handleActionClick.bind(this);
    }

    connectedCallback() {
      /*
       * Show native sharing only on supported devices.
       */
      if (
        this.nativeShareButton &&
        typeof navigator.share === 'function'
      ) {
        this.nativeShareButton.hidden = false;
      }

      if (this.trigger) {
        this.trigger.addEventListener(
          'click',
          this.handleTriggerClick
        );
      }

      if (this.modal) {
        this.modal.addEventListener(
          'click',
          this.handleModalClick
        );

        document.addEventListener(
          'keydown',
          this.handleKeydown
        );
      }

      this.actionButtons.forEach((button) => {
        button.addEventListener(
          'click',
          this.handleActionClick
        );
      });
    }

    disconnectedCallback() {
      if (this.trigger) {
        this.trigger.removeEventListener(
          'click',
          this.handleTriggerClick
        );
      }

      if (this.modal) {
        this.modal.removeEventListener(
          'click',
          this.handleModalClick
        );

        document.removeEventListener(
          'keydown',
          this.handleKeydown
        );
      }

      this.actionButtons.forEach((button) => {
        button.removeEventListener(
          'click',
          this.handleActionClick
        );
      });

      window.clearTimeout(this.statusTimer);
    }

    getReadableUrl(rawUrl) {
      try {
        return decodeURI(rawUrl);
      } catch (error) {
        return rawUrl;
      }
    }

    getMessengerUrl(rawUrl) {
      try {
        return encodeURI(decodeURI(rawUrl));
      } catch (error) {
        try {
          return encodeURI(rawUrl);
        } catch (encodingError) {
          return rawUrl;
        }
      }
    }

    handleTriggerClick() {
      this.openModal();
    }

    handleActionClick(event) {
      this.handleAction(event.currentTarget);
    }

    openModal() {
      if (!this.modal) return;

      this.clearStatus();

      this.previouslyFocusedElement =
        document.activeElement;

      this.modal.hidden = false;

      document.documentElement.classList.add(
        'custom-native-share-is-open'
      );

      requestAnimationFrame(() => {
        this.modal.classList.add('is-open');

        const firstFocusable =
          this.dialog?.querySelector(
            'button:not([disabled]):not([hidden]), a[href]'
          );

        firstFocusable?.focus();
      });
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;

      this.modal.classList.remove('is-open');

      document.documentElement.classList.remove(
        'custom-native-share-is-open'
      );

      window.setTimeout(() => {
        this.modal.hidden = true;
        this.clearStatus();
        this.previouslyFocusedElement?.focus?.();
      }, 180);
    }

    handleModalClick(event) {
      if (
        event.target.closest(
          '[data-native-share-close]'
        )
      ) {
        this.closeModal();
      }
    }

    handleKeydown(event) {
      if (
        event.key === 'Escape' &&
        this.modal &&
        !this.modal.hidden
      ) {
        this.closeModal();
      }
    }

    handleAction(button) {
      const action =
        button.dataset.nativeShareAction;

      const readableMessage =
        `${this.title}\n${this.url}`;

      switch (action) {
        case 'whatsapp':
          this.openExternal(
            `https://wa.me/?text=${encodeURIComponent(
              readableMessage
            )}`
          );
          break;

        case 'messenger':
          this.shareToMessenger();
          break;

        case 'telegram':
          this.openExternal(
            `https://t.me/share/url?url=${encodeURIComponent(
              this.url
            )}&text=${encodeURIComponent(this.title)}`
          );
          break;

        case 'native':
          this.shareNative();
          break;

        case 'copy':
          this.copyUrl();
          break;

        default:
          break;
      }
    }

    async shareNative() {
      if (typeof navigator.share !== 'function') return;

      try {
        await navigator.share({
          title: this.title,
          text: `${this.title}\n${this.url}`
        });

        if (this.displayMode === 'popup') {
          this.closeModal();
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;

        this.showStatus(
          this.nativeShareErrorText,
          true
        );
      }
    }

    isAndroidDevice() {
      return /Android/i.test(
        navigator.userAgent
      );
    }

    getMessengerSendDialogUrl() {
      const appId = '2111768026039637';

      return (
        'https://www.facebook.com/dialog/send' +
        `?app_id=${encodeURIComponent(appId)}` +
        `&link=${encodeURIComponent(
          this.messengerUrl
        )}` +
        `&redirect_uri=${encodeURIComponent(
          this.messengerUrl
        )}` +
        '&display=popup'
      );
    }

    shareToMessenger() {
      const appId = '2111768026039637';

      const sendDialogUrl =
        this.getMessengerSendDialogUrl();

      /*
       * Android:
       * Attempt to open Messenger directly.
       */
      if (this.isAndroidDevice()) {
        const messengerIntent =
          'intent://share' +
          `?link=${encodeURIComponent(
            this.messengerUrl
          )}` +
          `&app_id=${encodeURIComponent(appId)}` +
          '#Intent;' +
          'scheme=fb-messenger;' +
          'package=com.facebook.orca;' +
          `S.browser_fallback_url=${encodeURIComponent(
            sendDialogUrl
          )};` +
          'end';

        window.location.href =
          messengerIntent;

        return;
      }

      /*
       * Desktop, iPhone and other devices.
       */
      const popupWidth = 1000;
      const popupHeight = 760;

      const popupLeft = Math.max(
        0,
        Math.round(
          window.screenX +
          (window.outerWidth - popupWidth) / 2
        )
      );

      const popupTop = Math.max(
        0,
        Math.round(
          window.screenY +
          (window.outerHeight - popupHeight) / 2
        )
      );

      const popup = window.open(
        sendDialogUrl,
        'messenger-share',
        [
          `width=${popupWidth}`,
          `height=${popupHeight}`,
          `left=${popupLeft}`,
          `top=${popupTop}`,
          'resizable=yes',
          'scrollbars=yes'
        ].join(',')
      );

      if (popup) {
        try {
          popup.opener = null;
        } catch (error) {
          /*
           * No action required.
           */
        }

        popup.focus();
        return;
      }

      window.location.href =
        sendDialogUrl;
    }

    async copyUrl() {
      try {
        if (
          navigator.clipboard?.writeText &&
          window.isSecureContext
        ) {
          await navigator.clipboard.writeText(
            this.url
          );
        } else {
          const copied =
            this.legacyCopy(this.url);

          if (!copied) {
            throw new Error('Copy failed');
          }
        }

        this.showStatus(
          this.copySuccessText
        );
      } catch (error) {
        this.showStatus(
          this.copyErrorText,
          true
        );
      }
    }

    legacyCopy(text) {
      try {
        const textarea =
          document.createElement('textarea');

        textarea.value = text;

        textarea.setAttribute(
          'aria-hidden',
          'true'
        );

        textarea.style.position = 'fixed';
        textarea.style.insetInlineStart = '-9999px';
        textarea.style.top = '0';
        textarea.style.opacity = '0';
        textarea.style.pointerEvents = 'none';

        document.body.appendChild(textarea);

        textarea.focus();
        textarea.select();

        textarea.setSelectionRange(
          0,
          textarea.value.length
        );

        const copied =
          document.execCommand('copy');

        textarea.remove();

        return copied;
      } catch (error) {
        return false;
      }
    }

    openExternal(url) {
      const externalWindow =
        window.open(
          url,
          '_blank',
          'noopener,noreferrer'
        );

      if (!externalWindow) {
        window.location.href = url;
      }
    }

    showStatus(message, isError = false) {
      if (!this.status) return;

      window.clearTimeout(this.statusTimer);

      this.status.textContent =
        message;

      this.status.classList.toggle(
        'is-error',
        isError
      );

      this.status.hidden = false;

      /*
       * Inline messages disappear automatically.
       */
      if (this.displayMode === 'inline') {
        this.statusTimer = window.setTimeout(() => {
          this.clearStatus();
        }, 2500);
      }
    }

    clearStatus() {
      if (!this.status) return;

      window.clearTimeout(this.statusTimer);

      this.status.textContent = '';

      this.status.classList.remove(
        'is-error'
      );

      this.status.hidden = true;
    }
  }

  customElements.define(
    'custom-native-share',
    CustomNativeShare
  );
})();